/**
 * Save data or a URL as a file through a transient `<a download>`.
 *
 * The object URL is revoked shortly after the click, not in the same tick:
 * some browsers read the URL asynchronously and abort a download whose URL
 * was revoked right away. Revoking at all matters — every forgotten object
 * URL pins its blob in memory until the page is closed.
 */

/** How long a download's object URL stays alive after the click. */
export const NX_DOWNLOAD_REVOKE_DELAY = 1_000;

function clickLink(href: string, fileName: string | undefined): void {
  const link = document.createElement('a');
  link.href = href;
  if (fileName !== undefined) link.download = fileName;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Save `data` as `fileName`. A `Blob` is saved as it is (re-typed when `mime`
 * is given); anything else — text, a buffer — becomes a blob of type `mime`,
 * which defaults to UTF-8 plain text.
 */
export function downloadBlob(data: Blob | BlobPart, fileName: string, mime?: string): void {
  if (typeof document === 'undefined') return;
  const blob =
    data instanceof Blob
      ? mime && mime !== data.type
        ? new Blob([data], { type: mime })
        : data
      : new Blob([data], { type: mime ?? 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    clickLink(url, fileName);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), NX_DOWNLOAD_REVOKE_DELAY);
  }
}

/**
 * Save the file behind `url`. The browser only honours `fileName` for
 * same-origin URLs (and `blob:`/`data:` ones); cross-origin it navigates or
 * uses the server's name.
 */
export function downloadUrl(url: string, fileName?: string): void {
  if (typeof document === 'undefined') return;
  clickLink(url, fileName ?? '');
}
